import React, { useState } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DownloadGate = ({ 
  isOpen, 
  onClose, 
  downloadUrl, 
  fileName, 
  resourceType = 'material', // 'material', 'pdf', 'video', 'pyq'
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.name || !formData.email || !formData.contact) {
      setError('All fields are required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Phone validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.contact.replace(/[^0-9]/g, ''))) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setSubmitting(true);

    try {
      // Save user information to database
      const response = await fetch(`${API_URL}/api/download-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          contact: formData.contact,
          resource_type: resourceType,
          file_name: fileName,
          download_url: downloadUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit information');
      }

      // Proceed with download
      window.open(downloadUrl, '_blank');

      // Notify parent (e.g. for download tracking)
      if (typeof onSuccess === 'function') {
        try { onSuccess(); } catch (e) { /* swallow */ }
      }

      // Close modal and reset form
      setTimeout(() => {
        onClose();
        setFormData({ name: '', email: '', contact: '' });
      }, 500);

    } catch (err) {
      console.error('Error:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl">
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
          <CardTitle className="flex items-center text-red-600">
            <Download className="mr-2" size={24} />
            Download Resource
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Please provide your details to download this resource
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Number *
              </label>
              <input
                type="tel"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="10-digit mobile number"
                required
                maxLength="10"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 p-3 rounded-md text-sm text-blue-800">
              <p className="font-medium mb-1">Why we collect this information:</p>
              <p className="text-xs">We use your details to send you important updates, study materials, and exam notifications. Your privacy is important to us.</p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={20} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Download className="mr-2" size={20} />
                    Download Now
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DownloadGate;
